/**
 * Current-user API — /api/v1/me
 *
 * Called once after MSAL login to trigger JIT user registration (upserts a
 * kodex_users row) and return the caller's provider scope.
 *
 * Auth is handled via the shared token registered through registerTokenProvider
 * in api.ts. The call is best-effort — a failure here must never block the
 * login flow.
 */

import { authHeaders } from "@/services/api";
import { appConfig } from "@/config/app";

export interface MeResponse {
  user_id: string;
  email: string | null;
  // App roles from the JWT (app_service) or SCIM groups (databricks_apps).
  roles: string[];
  provider_ids: string[];
}

export interface PlatformUser {
  user_id: string;
  email: string | null;
  is_active: boolean;
}

/**
 * GET /api/v1/me
 * Upserts the current user into kodex_users and returns their identity +
 * linked provider IDs.  Call once after MSAL login.
 */
export async function fetchMe(): Promise<MeResponse> {
  const headers = await authHeaders();
  const res = await fetch("/api/v1/me", { headers });
  if (!res.ok) {
    throw new Error(`GET /api/v1/me failed: HTTP ${res.status}`);
  }
  return res.json() as Promise<MeResponse>;
}

/**
 * GET /api/v2/users?not_linked_to={providerId}&search={fragment}
 * Returns registered platform users available to link to a provider.
 * Accessible to global App.ADMIN, or to a provider-scoped UPA admin for the
 * given providerId (backend requires not_linked_to).
 */
export async function fetchAvailableUsers(
  providerId: string,
  search?: string
): Promise<PlatformUser[]> {
  const headers = await authHeaders();
  const qs = new URLSearchParams({ not_linked_to: providerId });
  if (search) qs.set("search", search);
  const res = await fetch(`${appConfig.api.v2BaseUrl}/users?${qs}`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string })?.detail ?? `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return res.json() as Promise<PlatformUser[]>;
}

/**
 * GET /api/v2/users — full tenant directory (App.ADMIN / PROVIDER_WRITE only).
 * Used by pipeline board admin filters.
 */
export async function fetchPlatformUsers(search?: string): Promise<PlatformUser[]> {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  const suffix = qs.toString() ? `?${qs}` : "";
  const res = await fetch(`${appConfig.api.v2BaseUrl}/users${suffix}`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string })?.detail ?? `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return res.json() as Promise<PlatformUser[]>;
}
